import {Button} from '@mui/material';
import React, {useCallback, useEffect, useMemo} from 'react';
import {useTranslation} from 'react-i18next';
import {ReactComponent as EditIcon} from '../../assets/icons/edit.svg';
import {ReactComponent as MoreIcon} from '../../assets/icons/more.svg';
import {ReactComponent as PlusIcon} from '../../assets/icons/plus.svg';
import {ReactComponent as TrashIcon} from '../../assets/icons/trash.svg';
import {useConversation} from '../../contexts/ConversationContext';
import {useCollectionActions} from '../../hooks/useCollectionActions';
import {DataSource, DocumentInDataSource} from '../../models/models';
import AddDocumentDialog from '../data-sources/AddDocumentDialog';
import {ContextMenu, ContextMenuItem, OptionsButton} from '../data-sources/DataSourceTable.styled';
import ConfirmationDialog from '../dialogs/ConfirmationDialog';
import GenericInputDialog from '../dialogs/InputDialog';
import DataSourceMultiSelect from './DataSourceMultiSelect';
import FileCard from './FileCard';
import {
    EmptyState,
    EmptyStateText,
    FilesGrid,
    PanelContainer,
    PanelHeader,
    ScrollableContent,
    SectionDivider,
    SourceActions,
    SourceHeader,
    SourceName,
    SourceSection
} from './FileSelectionPanel.styled';

type Props = {
    isOpen: boolean;
    dataSources: DataSource[];
    itemsByGroup: Record<string, DocumentInDataSource[]>;
    loadingGroupIds: (number | string)[];
    onLoadGroupItems: (groupId: number) => void;
    isLoadingDataSources: boolean;
    onDataChanged?: () => void;
};

const FileSelectionPanel: React.FC<Props> = ({
                                                 isOpen,
                                                 dataSources,
                                                 itemsByGroup,
                                                 loadingGroupIds,
                                                 onLoadGroupItems,
                                                 isLoadingDataSources,
                                                 onDataChanged
                                             }) => {
    const {t} = useTranslation();
    const {state, updateCollectionsFromUser, updateDocumentsFromUser} = useConversation();
    const {selectedGroupIds, selectedDocIds} = state;

    const handleSourceDeleted = useCallback((sourceId: number) => {
        const newGroupIds = selectedGroupIds.filter(id => id !== sourceId);
        updateCollectionsFromUser(newGroupIds);
    }, [selectedGroupIds, updateCollectionsFromUser]);

    const handleDocumentDeleted = useCallback((documentId: number) => {
        const newDocIds = selectedDocIds.filter(id => id !== documentId);
        updateDocumentsFromUser(newDocIds);
    }, [selectedDocIds, updateDocumentsFromUser]);

    const handleRefreshSource = useCallback((sourceId: number) => {
        onLoadGroupItems(sourceId);
    }, [onLoadGroupItems]);

    const {
        sourceContextMenu,
        documentContextMenu,
        editNameDialog,
        addDocumentDialog,
        deleteSourceDialog,
        deleteDocumentDialog,
        openSourceContextMenu,
        closeSourceContextMenu,
        openDocumentContextMenu,
        closeDocumentContextMenu,
        openEditNameDialog,
        openAddDocumentDialog,
        openDeleteSourceDialog,
        openDeleteDocumentDialog,
        closeEditNameDialog,
        closeDeleteSourceDialog,
        closeDeleteDocumentDialog,
        updateSourceName,
        deleteSource,
        addDocuments,
        deleteDocument,
    } = useCollectionActions({
        onDataChanged,
        onSourceDeleted: handleSourceDeleted,
        onDocumentDeleted: handleDocumentDeleted,
        onRefreshSource: handleRefreshSource,
    });

    useEffect(() => {
        selectedGroupIds.forEach(groupId => {
            if (!itemsByGroup[String(groupId)] && !loadingGroupIds.includes(groupId)) {
                onLoadGroupItems(groupId);
            }
        });
    }, [selectedGroupIds, itemsByGroup, loadingGroupIds, onLoadGroupItems]);

    const selectedDataSources = useMemo(() => {
        return dataSources.filter(ds => selectedGroupIds.includes(ds.id));
    }, [dataSources, selectedGroupIds]);

    const sortedDataSources = useMemo(() => {
        return [...selectedDataSources].sort((a, b) => {
            const docsA = itemsByGroup[String(a.id)] || [];
            const docsB = itemsByGroup[String(b.id)] || [];

            if (docsA.length > 0 && docsB.length === 0) return -1;
            if (docsA.length === 0 && docsB.length > 0) return 1;
            return 0;
        });
    }, [selectedDataSources, itemsByGroup]);

    const handleDataSourceSelect = useCallback((ids: number[]) => {
        const newGroupIds = ids.filter(id =>
            dataSources.some(ds => ds.id === id)
        );

        newGroupIds.forEach(groupId => {
            if (!itemsByGroup[String(groupId)] && !loadingGroupIds.includes(groupId)) {
                onLoadGroupItems(groupId);
            }
        });

        updateCollectionsFromUser(newGroupIds);
    }, [dataSources, itemsByGroup, loadingGroupIds, onLoadGroupItems, updateCollectionsFromUser]);

    const handleFileToggle = useCallback((docId: number) => {
        const isCurrentlySelected = selectedDocIds.includes(docId);

        if (isCurrentlySelected) {
            const newDocIds = selectedDocIds.filter(id => id !== docId);
            updateDocumentsFromUser(newDocIds);
        } else {
            const newDocIds = [...selectedDocIds, docId];
            updateDocumentsFromUser(newDocIds);
        }
    }, [selectedDocIds, updateDocumentsFromUser]);

    const handleSelectAllInSource = useCallback((sourceId: number) => {
        const docs = itemsByGroup[String(sourceId)] || [];
        const docIds = docs.map(d => d.id);

        const otherDocs = selectedDocIds.filter(id =>
            !docIds.includes(id)
        );

        const newDocIds = [...otherDocs, ...docIds];
        updateDocumentsFromUser(newDocIds);
    }, [itemsByGroup, selectedDocIds, updateDocumentsFromUser]);

    const handleDeselectAllInSource = useCallback((sourceId: number) => {
        const docs = itemsByGroup[String(sourceId)] || [];
        const docIds = new Set(docs.map(d => d.id));

        const newDocIds = selectedDocIds.filter(id => !docIds.has(id));
        updateDocumentsFromUser(newDocIds);
    }, [itemsByGroup, selectedDocIds, updateDocumentsFromUser]);

    const getSelectedCountInSource = useCallback((sourceId: number) => {
        const docs = itemsByGroup[String(sourceId)] || [];
        return docs.filter(doc => selectedDocIds.includes(doc.id)).length;
    }, [itemsByGroup, selectedDocIds]);

    const handleDocumentOptionsClick = useCallback((event: React.MouseEvent, document: DocumentInDataSource, sourceId: number) => {
        openDocumentContextMenu(event, document, sourceId);
    }, [openDocumentContextMenu]);

    const handleDocumentCreated = useCallback(() => {
        if (addDocumentDialog) {
            onLoadGroupItems(Number(addDocumentDialog.sourceId));
        }
        addDocuments();
    }, [addDocumentDialog, onLoadGroupItems, addDocuments]);

    const hasAnySelectedSources = selectedDataSources.length > 0;

    return (
        <PanelContainer $isOpen={isOpen}>
            <PanelHeader>
                <DataSourceMultiSelect
                    dataSources={dataSources}
                    selectedIds={selectedGroupIds}
                    onChange={handleDataSourceSelect}
                />
            </PanelHeader>

            <ScrollableContent $isEmpty={!hasAnySelectedSources}>
                {!hasAnySelectedSources && !isLoadingDataSources && (
                    <EmptyState>
                        <EmptyStateText>
                            {t('fileSelection.noSourcesSelected', {defaultValue: 'Wybierz źródła danych aby zobaczyć pliki'})}
                        </EmptyStateText>
                    </EmptyState>
                )}

                {sortedDataSources.map((source, index) => {
                    const docs = itemsByGroup[String(source.id)] || [];
                    const selectedCount = getSelectedCountInSource(source.id);
                    const allSelected = docs.length > 0 && selectedCount === docs.length;

                    return (
                        <React.Fragment key={source.id}>
                            {index > 0 && <SectionDivider/>}
                            <SourceSection>
                                <SourceHeader>
                                    <SourceName>{source.name}</SourceName>
                                    <SourceActions>
                                        {docs.length > 0 && (
                                            <Button
                                                size="small"
                                                onClick={() => allSelected ? handleDeselectAllInSource(source.id) : handleSelectAllInSource(source.id)}
                                                aria-label={allSelected ? t('fileSelection.clearSelection') as string : t('fileSelection.selectAll') as string}
                                            >
                                                {allSelected ? t('fileSelection.clearSelection') : t('fileSelection.selectAll')}
                                            </Button>
                                        )}
                                        <OptionsButton
                                            size="small"
                                            style={{width: 26}}
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                openSourceContextMenu(e, source);
                                            }}
                                        >
                                            <MoreIcon/>
                                        </OptionsButton>
                                    </SourceActions>
                                </SourceHeader>

                                {docs.length === 0 && !loadingGroupIds.includes(source.id) ? (
                                    <EmptyState>
                                        <EmptyStateText>
                                            {t('fileSelection.noFiles', {defaultValue: 'Brak plików w tym źródle'})}
                                        </EmptyStateText>
                                    </EmptyState>
                                ) : (
                                    <FilesGrid>
                                        {docs.map(doc => (
                                            <FileCard
                                                key={doc.id}
                                                document={doc}
                                                selected={selectedDocIds.includes(doc.id)}
                                                onToggle={handleFileToggle}
                                                onOptionsClick={(e) => handleDocumentOptionsClick(e, doc, source.id)}
                                            />
                                        ))}
                                    </FilesGrid>
                                )}
                            </SourceSection>
                        </React.Fragment>
                    );
                })}
            </ScrollableContent>

            <ContextMenu
                open={sourceContextMenu !== null}
                onClose={closeSourceContextMenu}
                anchorReference="anchorPosition"
                anchorPosition={
                    sourceContextMenu !== null
                        ? {top: sourceContextMenu.mouseY, left: sourceContextMenu.mouseX}
                        : undefined
                }
            >
                {sourceContextMenu && [
                    <ContextMenuItem key="edit" onClick={() => openEditNameDialog(sourceContextMenu.source)}>
                        <EditIcon style={{marginRight: '8px', width: '16px', height: '16px'}}/>
                        {t('fileSelection.editName')}
                    </ContextMenuItem>,
                    <ContextMenuItem key="add" onClick={() => openAddDocumentDialog(sourceContextMenu.source)}>
                        <PlusIcon style={{marginRight: '8px', width: '16px', height: '16px'}}/>
                        {t('fileSelection.addDocuments')}
                    </ContextMenuItem>,
                    <ContextMenuItem
                        key="delete"
                        onClick={() => openDeleteSourceDialog(sourceContextMenu.source)}
                        style={{color: '#f44336'}}
                    >
                        <TrashIcon style={{marginRight: '8px', width: '16px', height: '16px'}}/>
                        {t('fileSelection.deleteSource')}
                    </ContextMenuItem>,
                ]}
            </ContextMenu>

            <ContextMenu
                open={documentContextMenu !== null}
                onClose={closeDocumentContextMenu}
                anchorReference="anchorPosition"
                anchorPosition={
                    documentContextMenu !== null
                        ? {top: documentContextMenu.mouseY, left: documentContextMenu.mouseX}
                        : undefined
                }
            >
                {documentContextMenu && [
                    <ContextMenuItem
                        key="delete"
                        onClick={() => openDeleteDocumentDialog(documentContextMenu.document, documentContextMenu.sourceId)}
                        style={{color: '#f44336'}}
                    >
                        <TrashIcon style={{marginRight: '8px', width: '16px', height: '16px'}}/>
                        {t('fileSelection.deleteDocument')}
                    </ContextMenuItem>,
                ]}
            </ContextMenu>

            {addDocumentDialog && (
                <AddDocumentDialog
                    open={true}
                    onClose={() => addDocuments()}
                    dataSourceId={addDocumentDialog.sourceId}
                    onDocumentCreated={handleDocumentCreated}
                />
            )}

            {editNameDialog && (
                <GenericInputDialog
                    open={true}
                    title={t('fileSelection.editNameTitle') as string}
                    label={t('fileSelection.editNameLabel') as string}
                    required
                    initialValue={editNameDialog.currentName}
                    onClose={closeEditNameDialog}
                    onConfirm={updateSourceName}
                />
            )}

            {deleteSourceDialog && (
                <ConfirmationDialog
                    open={true}
                    onClose={closeDeleteSourceDialog}
                    onConfirm={deleteSource}
                    title={t('fileSelection.deleteSourceTitle') as string}
                    message={<>{t('fileSelection.deleteSourceMessage')} &ldquo;{deleteSourceDialog.source.name}&rdquo;?</>}
                    confirmButtonText={t('fileSelection.deleteButton') as string}
                    cancelButtonText={t('fileSelection.cancelButton') as string}
                />
            )}

            {deleteDocumentDialog && (
                <ConfirmationDialog
                    open={true}
                    onClose={closeDeleteDocumentDialog}
                    onConfirm={deleteDocument}
                    title={t('fileSelection.deleteDocumentTitle') as string}
                    message={<>{t('fileSelection.deleteDocumentMessage')} &ldquo;{deleteDocumentDialog.document.name}&rdquo;?</>}
                    confirmButtonText={t('fileSelection.deleteButton') as string}
                    cancelButtonText={t('fileSelection.cancelButton') as string}
                />
            )}
        </PanelContainer>
    );
};

export default FileSelectionPanel;
